import { InfoCircleOutlined, MoreOutlined, PlusCircleFilled } from "@ant-design/icons";
import { useSelect } from "@refinedev/antd";
import { HttpError, useApiUrl, useCustom, useCustomMutation, useTranslate, useWarnAboutChange } from "@refinedev/core";
import { App, Button, Col, Dropdown, Form, Popover, Row, Select, Skeleton, Spin, Tooltip, Typography } from "antd";
import { API_RESOURCES } from "config";
import dayjs from "dayjs";
import { IClassPeriod, ISubject, IUser } from "interfaces";
import { useCallback, useMemo, useRef, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useParams } from "react-router-dom";
import { formatUser } from "utils/modelHelper";
import { fuzzySearch } from "utils/searchHelper";
import { formatTimePeriodString } from "utils/timeHelpers";
import { toLowerCaseNonAccentVietnamese } from "utils/vietnameseHelper";

const { Text, Title } = Typography;
interface ScheduleClassProps {
  [key: string]: any;
}
interface ISchedule {
  id?: string;
  classId?: string;
  classPeriodId?: string;
  teacherId?: string;
  subjectId?: string;
  dayOfWeek?: number;
  subject?: ISubject;
  teacher?: IUser;
}
const DragItem = ({ schedule }: { schedule: ISchedule }) => {
  const t = useTranslate();

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: "item",
      item: schedule,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [schedule]
  );

  return (
    <div
      className="card-schedule"
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
      }}
    >
      {schedule?.subject?.name && <Text strong>{schedule?.subject?.name}</Text>}
      <Text>
        {t("normal_class.schedule.label.teacher")}:{" "}
        {schedule?.teacher?.fullName && (
          <>
            <Text strong>{schedule?.teacher?.fullName} </Text>
            <Tooltip
              placement="bottom"
              title={
                (schedule?.teacher?.email || schedule?.teacher?.mobile) && (
                  <>
                    {schedule?.teacher?.email && <p style={{ marginBottom: 0 }}>Email: {schedule?.teacher?.email}</p>}
                    {schedule?.teacher?.mobile && (
                      <p style={{ marginBottom: 0 }}>Số điện thoại: {schedule?.teacher?.mobile}</p>
                    )}
                  </>
                )
              }
            >
              <InfoCircleOutlined />
            </Tooltip>
          </>
        )}
      </Text>
    </div>
  );
};
const DropZone = ({ onDrop }: { onDrop: (item: ISchedule) => void }) => {
  // Using useRef to hold the latest onDrop function
  const latestOnDrop = useRef(onDrop);

  // Ensure latest onDrop is used
  latestOnDrop.current = onDrop;

  // Wrapping drop handler with useCallback to ensure latest state is captured
  const handleDrop = useCallback((item: ISchedule | null) => {
    if (item === null) {
      throw new Error("Cannot drop a null item");
    }
    latestOnDrop.current(item);
  }, []);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "item",
      drop: handleDrop,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [handleDrop]
  );
  return (
    <div
      ref={drop}
      style={{
        backgroundColor: isOver ? "lightgreen" : "transparent",
        width: "100%",
        height: "100%",
      }}
    />
  );
};
export const ScheduleClass: React.FC<ScheduleClassProps> = ({ isView }) => {
  const [visible, setVisible] = useState<ISchedule | null>(null);
  const [itemLoading, setItemLoading] = useState<ISchedule | null>(null);
  const dayOfWeekList = ["title", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
  const apiUrl = useApiUrl();
  const { id } = useParams();
  const t = useTranslate();
  const { setWarnWhen } = useWarnAboutChange();
  const { modal } = App.useApp();

  const { data, refetch } = useCustom<any>({
    url: `${apiUrl}/class_time_table/${id}`,
    method: "get",
  });
  const { data: classPeriods, isLoading: loadingClassPeriod } = useCustom<any>({
    url: `${apiUrl}/class_period`,
    method: "get",
    config: {
      query: {
        page: 0,
        size: 1000000,
        orderBy: "-startTime",
      },
    },
  });
  const scheduleList = useMemo(() => data?.data, [data]);

  const classPeriodList = useMemo(() => classPeriods?.data?.content, [classPeriods]);
  // Select Môn học
  const {
    selectProps: { onSearch: onSearchSubject, ...subjectSelectProps },
    queryResult: subjectQueryResult,
  } = useSelect<ISubject, HttpError>({
    resource: `${API_RESOURCES.subject}`,
    optionLabel: "name",
    pagination: {
      current: 1,
      pageSize: 99999,
      mode: "server",
    },
  });

  const subjectSelectOptions = useMemo(
    () =>
      subjectQueryResult?.data?.data.map((subject) => ({
        value: subject?.id,
        label: subject?.name,
      })),
    [subjectQueryResult?.data?.data]
  );
  // Select Giáo viên
  const {
    selectProps: { onSearch: onSearchTeacher, ...teacherSelectProps },
    queryResult: teacherQueryResult,
  } = useSelect<IUser, HttpError>({
    resource: `${API_RESOURCES.users}/teachers`,
    optionLabel: "fullName",
    pagination: false,
  });

  const teacherSelectOptions = useMemo(
    () =>
      teacherQueryResult?.data?.data
        .sort((a, b) => (a.fullName ?? "").localeCompare(b.fullName ?? ""))
        .map((teacher) => ({
          value: teacher?.id,
          label: formatUser(teacher),
        })),
    [teacherQueryResult?.data?.data]
  );
  const { mutate: mutationScheduleClass, isLoading } = useCustomMutation<ISchedule>();

  const handleSubmitScheduleClass = async (values: ISchedule) => {
    try {
      if (visible?.id) {
        mutationScheduleClass(
          {
            url: `${apiUrl}/class_time_table/update`,
            method: "put",
            values: {
              id: visible.id,
              teacherId: values.teacherId,
              subjectId: values.subjectId,
            },
            errorNotification(error) {
              return {
                type: "error",
                message: error?.message ?? t("normal_class.schedule.notifications.editError"),
              };
            },
            successNotification: {
              type: "success",
              message: t("normal_class.schedule.notifications.editSuccess"),
            },
          },
          {
            onSuccess() {
              setWarnWhen?.(false);
              setVisible(null);
              setTimeout(() => {
                refetch();
              }, 100);
            },
          }
        );
      } else {
        createSchedule({
          classId: id,
          classPeriodId: visible?.classPeriodId,
          teacherId: values.teacherId,
          subjectId: values.subjectId,
          dayOfWeek: visible?.dayOfWeek,
        });
      }
    } catch (error) {
      console.log("🚀 ~ handleSubmitScheduleClass ~ error:", error);
    }
  };
  const createSchedule = async (payload: ISchedule) => {
    setItemLoading(payload);
    mutationScheduleClass(
      {
        url: `${apiUrl}/class_time_table/create`,
        method: "post",
        values: payload,
        errorNotification(error) {
          return {
            type: "error",
            message: error?.message ?? t("normal_class.schedule.notifications.createError"),
          };
        },
        successNotification: {
          type: "success",
          message: t("normal_class.schedule.notifications.createSuccess"),
        },
      },
      {
        onSuccess() {
          setWarnWhen?.(false);
          setVisible(null);
          setItemLoading(null);
          setTimeout(() => {
            refetch();
          }, 100);
        },
      }
    );
  };
  const handleDelete = async (id: string | undefined) => {
    if (!id) {
      return;
    }

    try {
      modal.confirm({
        icon: null,
        title: <Typography.Title level={5}>{t("normal_class.schedule.dialogs.titleDelete")}</Typography.Title>,
        content: <Typography.Text>{t("normal_class.schedule.dialogs.contentDelete")}</Typography.Text>,
        okText: t("buttons.agree"),
        cancelText: t("buttons.cancel"),
        onOk() {
          mutationScheduleClass(
            {
              url: `${apiUrl}/class_time_table/${id}`,
              method: "delete",
              values: {},
              errorNotification: (error) => ({
                type: "error",
                message: error?.message ?? t("normal_class.schedule.notifications.deleteError"),
              }),
              successNotification: {
                type: "success",
                message: t("normal_class.schedule.notifications.deleteSuccess"),
              },
            },
            {
              onSuccess() {
                setWarnWhen?.(false);
                setVisible(null);
                setTimeout(() => {
                  refetch();
                }, 100);
              },
            }
          );
        },
      });
    } catch (error) {
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const FormItem = (
    <>
      <Form
        name="add-schedule"
        labelCol={{
          span: 24,
        }}
        wrapperCol={{
          span: 24,
        }}
        autoComplete="off"
        onFinish={(values) => handleSubmitScheduleClass(values)}
        initialValues={{
          subjectId: visible?.subject?.id,
          teacherId: visible?.teacher?.id,
        }}
      >
        <Form.Item
          label={t("normal_class.schedule.fields.subject")}
          name={["subjectId"]}
          rules={[
            {
              required: true,
              message: t("normal_class.schedule.select.subject"),
            },
          ]}
        >
          <Select
            allowClear
            placeholder={t("normal_class.schedule.select.subject")}
            {...subjectSelectProps}
            options={subjectSelectOptions}
            showSearch
            filterOption={(input, option) =>
              fuzzySearch(
                toLowerCaseNonAccentVietnamese(String(option?.label ?? "")),
                toLowerCaseNonAccentVietnamese(input)
              )
            }
          />
        </Form.Item>

        <Form.Item
          label={t("normal_class.schedule.fields.teacher")}
          name={["teacherId"]}
          rules={[
            {
              required: true,
              message: t("normal_class.schedule.select.teacher"),
            },
          ]}
        >
          <Select
            allowClear
            placeholder={t("normal_class.schedule.select.teacher")}
            {...teacherSelectProps}
            options={teacherSelectOptions}
            showSearch
            filterOption={(input, option) =>
              fuzzySearch(
                toLowerCaseNonAccentVietnamese(String(option?.label ?? "")),
                toLowerCaseNonAccentVietnamese(input)
              )
            }
          />
        </Form.Item>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="text" onClick={() => setVisible(null)}>
            Hủy
          </Button>
          <Button type="primary" htmlType="submit">
            Lưu
          </Button>
        </div>
      </Form>
    </>
  );
  const findLesson = (dayOfWeek: number, period: IClassPeriod | undefined) => {
    let schedule: ISchedule | undefined;
    try {
      schedule = scheduleList?.find((f: ISchedule) => f.dayOfWeek === dayOfWeek && period?.id === f.classPeriodId);
    } catch (error) {
      console.log("🚀 ~ findLesson ~ error:", error);
    }
    if (schedule) {
      return (
        <>
          <div className="box-value">
            <div style={{ alignSelf: "flex-end" }}>
              <Popover
                open={period?.id === visible?.classPeriodId && visible?.dayOfWeek === dayOfWeek}
                trigger="click"
                content={FormItem}
                title={t("normal_class.schedule.title_modal")}
              >
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "1",
                        label: t("common.update"),
                        disabled: isView,
                        onClick: () =>
                          setVisible({
                            dayOfWeek,
                            classPeriodId: period?.id,
                            subject: schedule?.subject,
                            teacher: schedule?.teacher,
                            id: schedule?.id,
                          }),
                      },
                      {
                        key: "2",
                        label: t("common.delete"),
                        disabled: isView,
                        onClick: () => handleDelete(schedule?.id),
                      },
                    ],
                  }}
                  placement="bottomLeft"
                >
                  <MoreOutlined />
                </Dropdown>
              </Popover>
            </div>
            <DragItem schedule={schedule} />
          </div>
        </>
      );
    } else {
      return null;
    }
  };
  const handleDrop = (item: any) => {
    if (!isView && item?.dayOfWeek !== 0) {
      createSchedule({
        classId: id,
        classPeriodId: item?.classPeriodId,
        teacherId: item?.teacher?.id,
        subjectId: item?.subject?.id,
        dayOfWeek: item?.dayOfWeek,
      });
    }
  };
  return (
    <DndProvider backend={HTML5Backend}>
      <Title level={4} className="d-flex">
        Năm học {dayjs().format("YYYY")}-{dayjs().format("YYYY")}
      </Title>
      <Row justify="space-between" gutter={[8, 8]} className="classPeriod">
        {dayOfWeekList?.map((m, i) => (
          <Col span={3}>
            <div className="box-weekdays" style={{ background: i > 0 ? "#EFF6FF" : "" }}>
              <Title level={5}>{t(`normal_class.schedule.dayOfWeek.${m}`)}</Title>
            </div>
          </Col>
        ))}
      </Row>
      {loadingClassPeriod && <Skeleton active={loadingClassPeriod} />}
      <div className="lesson">
        {classPeriodList?.map((period: IClassPeriod) => (
          <Row justify="space-between" key={period.id} gutter={[8, 8]} className="classPeriod">
            <>
              {dayOfWeekList?.map((dow, dayOfWeek) => (
                <Col span={3} key={dayOfWeek}>
                  <Spin
                    spinning={
                      isLoading && period.id === itemLoading?.classPeriodId && itemLoading?.dayOfWeek === dayOfWeek
                    }
                  >
                    {dayOfWeek && period?.id && findLesson(dayOfWeek, period) ? (
                      findLesson(dayOfWeek, period)
                    ) : (
                      <div className="box-schedule ">
                        {dayOfWeek === 0 &&
                          [formatTimePeriodString(period.startTime), formatTimePeriodString(period.endTime)].join("-")}

                        {!isView && dayOfWeek > 0 && (
                          <>
                            <DropZone onDrop={(item) => handleDrop({ ...item, dayOfWeek, classPeriodId: period.id })} />
                            <div className="box-add_schedule">
                              <Popover
                                open={period.id === visible?.classPeriodId && visible?.dayOfWeek === dayOfWeek}
                                trigger="click"
                                content={FormItem}
                                title={t("normal_class.schedule.title_modal")}
                              >
                                <PlusCircleFilled
                                  onClick={() => {
                                    setVisible({ dayOfWeek, classPeriodId: period.id });
                                  }}
                                  style={{ position: "relative" }}
                                  twoToneColor={"#00748F"}
                                  size={42}
                                />
                              </Popover>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </Spin>
                </Col>
              ))}
            </>
          </Row>
        ))}
      </div>
    </DndProvider>
  );
};
